import type { EventUser, GameEvent, GiftInfo, SourceStatus } from '@rekrut/shared';
import { Rng } from '../sim/rng.js';
import type { IEventSource } from './types.js';

/** Ручки интенсивности — крутятся из дев-панели прямо во время матча. */
export interface MockSettings {
  /** Общий множитель всех потоков. */
  intensity: number;
  /** Сколько зрителей «в комнате» — от него считается порог щита за лайки. */
  viewers: number;
  joinsPerMin: number;
  chatsPerMin: number;
  giftsPerMin: number;
  likesPerSec: number;
  followsPerMin: number;
  sharesPerMin: number;
  subsPerMin: number;
}

export const defaultMockSettings: MockSettings = {
  intensity: 1,
  viewers: 1200,
  joinsPerMin: 80,
  chatsPerMin: 120,
  giftsPerMin: 5,
  likesPerSec: 15,
  followsPerMin: 6,
  sharesPerMin: 3,
  subsPerMin: 1.5,
};

/**
 * Распределение донатов. Хвост тяжёлый и редкий — как в реальном эфире:
 * подавляющее большинство подарков это розы за 1💎, а «Кара» за 5000💎
 * случается несколько раз за вечер и должна ощущаться событием.
 */
const GIFT_WEIGHTS: { min: number; max: number; w: number }[] = [
  { min: 1, max: 4, w: 62 },
  { min: 5, max: 29, w: 21 },
  { min: 30, max: 199, w: 11 },
  { min: 200, max: 999, w: 4.2 },
  { min: 1000, max: 4999, w: 1.4 },
  { min: 5000, max: 12000, w: 0.4 },
];

/** Ники нарочно разноязычные с эмодзи — это стресс-тест рендера текста. */
const NICK_PARTS_A = [
  'Дима', 'Настя', 'Кирилл', 'Лера', 'Тимур', 'Sasha', 'Nurlan', 'Айгуль', 'Ваня', 'Полина',
  'Marek', 'Ayşe', 'こうた', '민준', 'Диана', 'Егор', 'Zhanna', 'Артём', 'Соня', 'Rustam',
];
const NICK_PARTS_B = [
  '', '', '', '_2007', '.off', '777', '_life', 'kz', '_ua', '01', '_pro', 'xd', '_tv', '99',
];
const NICK_EMOJI = ['', '', '', '', '🔥', '🌸', '⚡', '👑', '🐺', '💫', '🖤', '🎮'];

const CHAT_LINES = [
  'А', 'Б', 'а', 'б', 'A', 'B', '1', '2',
  'давай наши', 'го камбэк', 'капитан держись', 'вернулся!!', 'что за игра',
  'ааа меня забрали', 'красные топ', 'синие рулят', 'хочу обратно', 'подписался',
  'кто со мной', 'жёстко', 'ща верну', 'лайкайте', '🔥🔥🔥', 'привет всем',
];

export class MockEventSource implements IEventSource {
  readonly name = 'mock';
  status: SourceStatus = 'idle';
  /**
   * Витрина подарков для демо.
   *
   * В эфире каталог приходит от коннектора вместе с иконками
   * (enableExtendedGiftInfo), и этот список подменяется целиком. Здесь он
   * нужен только чтобы прайс на экране выглядел как настоящий: по одному
   * самому дешёвому подарку на каждую ступень.
   */
  giftCatalog: GiftInfo[] = [
    { id: 5655, name: 'Роза', diamonds: 1, iconUrl: '' },
    { id: 5269, name: 'Палец-сердце', diamonds: 5, iconUrl: '' },
    { id: 5487, name: 'Пончик', diamonds: 30, iconUrl: '' },
    { id: 6247, name: 'Денежный пистолет', diamonds: 200, iconUrl: '' },
    { id: 6064, name: 'Галактика', diamonds: 1000, iconUrl: '' },
    { id: 6784, name: 'Лев', diamonds: 5000, iconUrl: '' },
  ];

  settings: MockSettings = { ...defaultMockSettings };
  /** Полностью останавливает генерацию, не разрывая «соединение». */
  paused = false;

  private rng = new Rng(20260727);
  private pool: EventUser[] = [];
  private evCb: ((e: GameEvent) => void) | null = null;
  private stCb: ((s: SourceStatus) => void) | null = null;
  private seq = 0;

  /** Накопители дробных «сколько событий за кадр». */
  private accJoin = 0;
  private accChat = 0;
  private accGift = 0;
  private accLike = 0;
  private accFollow = 0;
  private accShare = 0;
  private accSub = 0;
  private accViewers = 0;

  async connect(_streamerId: string): Promise<void> {
    this.setStatus('connecting');
    this.pool = [];
    for (let i = 0; i < 400; i++) this.pool.push(this.makeUser());
    this.setStatus('live');
  }

  disconnect(): void {
    this.setStatus('offline');
  }

  onEvent(cb: (e: GameEvent) => void): void {
    this.evCb = cb;
  }

  onStatus(cb: (s: SourceStatus) => void): void {
    this.stCb = cb;
  }

  tick(dtMs: number): void {
    if (this.paused || this.status !== 'live') return;
    const k = (this.settings.intensity * dtMs) / 1000;
    const s = this.settings;

    this.accJoin += (s.joinsPerMin / 60) * k;
    this.accChat += (s.chatsPerMin / 60) * k;
    this.accGift += (s.giftsPerMin / 60) * k;
    this.accLike += s.likesPerSec * k;
    this.accFollow += (s.followsPerMin / 60) * k;
    this.accShare += (s.sharesPerMin / 60) * k;
    this.accSub += (s.subsPerMin / 60) * k;
    this.accViewers += dtMs / 1000;

    while (this.accJoin >= 1) {
      this.accJoin -= 1;
      this.emitJoin();
    }
    while (this.accChat >= 1) {
      this.accChat -= 1;
      this.emitChat();
    }
    while (this.accGift >= 1) {
      this.accGift -= 1;
      this.emitGift();
    }
    while (this.accFollow >= 1) {
      this.accFollow -= 1;
      this.emit({ type: 'follow', ts: Date.now(), user: this.pickUser() });
    }
    while (this.accShare >= 1) {
      this.accShare -= 1;
      this.emit({ type: 'share', ts: Date.now(), user: this.pickUser() });
    }
    while (this.accSub >= 1) {
      this.accSub -= 1;
      this.emit({ type: 'subscribe', ts: Date.now(), user: this.pickUser() });
    }

    // Лайки уходят пачками раз в 500 мс — ровно как их отдаёт коннектор (§4.5).
    if (this.accLike >= 1) {
      const n = Math.floor(this.accLike);
      this.accLike -= n;
      this.emit({ type: 'like_batch', ts: Date.now(), likes: n });
    }

    if (this.accViewers >= 5) {
      this.accViewers = 0;
      const drift = Math.round(this.rng.jitter(this.settings.viewers * 0.06));
      this.emit({ type: 'viewers', ts: Date.now(), viewerCount: Math.max(0, this.settings.viewers + drift) });
    }
  }

  // ─── ручной ввод из дев-панели ─────────────────────────────────────────

  /** Отправить подарок на заданную сумму от случайного (или указанного) зрителя. */
  sendGift(diamonds: number, user?: EventUser): void {
    this.emit({
      type: 'gift',
      ts: Date.now(),
      user: user ?? this.pickUser(),
      giftId: 0,
      giftName: 'debug',
      diamonds,
      count: 1,
    });
  }

  /** Массовое вступление — быстро наполнить арену. */
  sendJoins(n: number, team?: 'A' | 'B'): void {
    for (let i = 0; i < n; i++) {
      const u = this.makeUser();
      this.pool.push(u);
      const t = team ?? (this.rng.bool() ? 'A' : 'B');
      this.emit({ type: 'chat', ts: Date.now(), user: u, comment: t === 'A' ? 'А' : 'Б' });
    }
  }

  // ─── внутреннее ────────────────────────────────────────────────────────

  private emitJoin(): void {
    const u = this.makeUser();
    this.pool.push(u);
    if (this.pool.length > 2000) this.pool.shift();
    this.emit({ type: 'member', ts: Date.now(), user: u });
    this.emit({
      type: 'chat',
      ts: Date.now(),
      user: u,
      comment: this.rng.bool() ? this.rng.pick(['А', 'A', '1']) : this.rng.pick(['Б', 'B', '2']),
    });
  }

  private emitChat(): void {
    this.emit({
      type: 'chat',
      ts: Date.now(),
      user: this.pickUser(),
      comment: this.rng.pick(CHAT_LINES),
    });
  }

  private emitGift(): void {
    let total = 0;
    for (const g of GIFT_WEIGHTS) total += g.w;
    let r = this.rng.range(0, total);
    let band = GIFT_WEIGHTS[0]!;
    for (const g of GIFT_WEIGHTS) {
      r -= g.w;
      if (r <= 0) {
        band = g;
        break;
      }
    }
    // Внутри полосы — тоже перекос к нижней границе.
    const t = this.rng.next() ** 2;
    const diamonds = Math.max(1, Math.round(band.min + (band.max - band.min) * t));
    this.sendGift(diamonds);
  }

  private pickUser(): EventUser {
    if (this.pool.length === 0) return this.makeUser();
    // Донатят и пишут чаще те, кто уже давно в комнате.
    const idx = Math.floor(this.pool.length * this.rng.next() ** 0.6);
    return this.pool[Math.min(idx, this.pool.length - 1)]!;
  }

  private makeUser(): EventUser {
    const n = ++this.seq;
    const nickname =
      this.rng.pick(NICK_PARTS_A) + this.rng.pick(NICK_PARTS_B) + this.rng.pick(NICK_EMOJI);
    return {
      userId: `mock:${n}`,
      uniqueId: `user${n}`,
      nickname,
      // Пусто → рендер рисует identicon. Это же путь для реальных 403 от CDN (§4.7).
      avatarUrl: '',
      isSubscriber: this.rng.bool(0.08),
      isModerator: false,
    };
  }

  private emit(e: GameEvent): void {
    e.streamerId = e.streamerId ?? 'demo';
    this.evCb?.(e);
  }

  private setStatus(s: SourceStatus): void {
    this.status = s;
    this.stCb?.(s);
  }
}
